import React, { useState } from 'react';
import { BodyText, Checkbox, Table } from '@gaiads/telia-react-component-library';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { BundleOverview, BundlePrice, BundlePriceTypeEnum, LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import LanguageList from '../../../shared/ListUtil/LanguageList';
import { PriceAmountInput } from './PriceAmountInput';
import { PriceSaveButton } from './PriceSaveButton';
import { PriceTypeDropdown } from './PriceTypeDropdown';
import { PriceVatInput } from './PriceVatInput';
import ArrowButton from '../../../shared/ListUtil/ArrowButton';

interface BundleListItemProps {
  children?: JSX.Element | JSX.Element[];
  item: BundleOverview;
  selected: boolean;
  locales: LocaleResponse[];
  open: boolean;
  onItemChecked: (bundleId: number) => void;
  onRowClick: (bundleId: number) => void;
  onOpenRow: (bundleId: number) => void;
  handlePriceSave: (bundleId: number, updatedBundlePrice: BundlePrice) => Promise<void>;
  salesChannelUsesDynamicPricing: boolean;
}

const BundleListItem = ({
  children,
  item,
  locales,
  selected,
  open,
  onItemChecked,
  onRowClick,
  onOpenRow,
  handlePriceSave,
  salesChannelUsesDynamicPricing,
}: BundleListItemProps) => {
  const { id, locale_ids, brand, name, price } = item;
  const [priceAmount, setPriceAmount] = useState(price && String(price.amount));
  const [priceType, setPriceType] = useState(price && String(price.type));
  const [priceVat, setPriceVat] = useState(price && String(price.vat_percentage));
  const [priceChanged, setPriceChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('bundlesview');
  const { t: tProductScreen } = useTranslation('productsscreen');

  const handlePriceTypeChange = (priceType: string) => {
    setPriceType(priceType);
    setPriceChanged(true);
  };
  const handlePriceAmountChange = (priceAmount: string) => {
    setPriceAmount(priceAmount);
    setPriceChanged(true);
  };
  const handlePriceVatChange = (priceVat: string) => {
    setPriceVat(priceVat);
    setPriceChanged(true);
  };

  const toggleListItem = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    onOpenRow(id);
  };

  const handleSaveClick = async () => {
    if (!price) {
      return;
    }
    setLoading(true);
    const bundlePrice: BundlePrice = {
      ...price,
      amount: Number(priceAmount),
      vat_percentage: Number(priceVat),
      type: priceType as BundlePriceTypeEnum,
    };
    await handlePriceSave(id, bundlePrice);
    setLoading(false);
    setPriceChanged(false);
  };

  const isPriceUpdateDateOld = (date: string) => {
    const oldThreshold = 13; // 14 days old price is old
    const today = DateTime.now();
    const updateDate = DateTime.fromISO(date);
    const priceAge = today.diff(updateDate, 'days');
    const priceObject = priceAge.toObject();
    if (priceObject && priceObject.days) {
      return priceObject.days > oldThreshold;
    } else {
      return false;
    }
  };

  return (
    <>
      <Table.Row className="telia-react-table_row__master">
        <Table.Cell type="checkbox">
          <Checkbox checked={selected} onChange={() => onItemChecked(id)} onClick={event => event.stopPropagation()} />
        </Table.Cell>
        <Table.Cell>
          <ArrowButton open={open} onClick={toggleListItem} />
        </Table.Cell>
        <Table.Cell className="daas-product-table__brand" onClick={() => onRowClick(id)}>
          <BodyText>{brand}</BodyText>
        </Table.Cell>
        <Table.Cell className="daas-product-table__name" onClick={() => onRowClick(id)}>
          <BodyText>{name}</BodyText>
        </Table.Cell>
        <Table.Cell></Table.Cell>
        <Table.Cell>
          <LanguageList languages={locale_ids} locales={locales} />
        </Table.Cell>
        {salesChannelUsesDynamicPricing && (
          <Table.Cell>
            <Checkbox data-testid="insurance-checkbox" checked={price?.insured || false} disabled={true} />
          </Table.Cell>
        )}
        <Table.Cell>
          {salesChannelUsesDynamicPricing ? (
            <BodyText>{tProductScreen(`priceTypes.${priceType}`)}</BodyText>
          ) : (
            <PriceTypeDropdown priceType={priceType} onPriceTypeChange={handlePriceTypeChange} />
          )}
        </Table.Cell>
        <Table.Cell>
          {salesChannelUsesDynamicPricing ? (
            <BodyText color={!item.valid_for_dynamic_pricing ? 'red600' : 'black'}>
              {item.valid_for_dynamic_pricing ? (item.price?.amount ?? '') : t('bundles_table.dynamic_pricing_not_setup_error')}
            </BodyText>
          ) : (
            <PriceAmountInput priceAmount={priceAmount} onPriceAmountChange={handlePriceAmountChange} />
          )}
        </Table.Cell>
        <Table.Cell>
          {salesChannelUsesDynamicPricing ? (
            item.valid_for_dynamic_pricing && (
              <BodyText color={isPriceUpdateDateOld(item.price?.updated || '') ? 'red600' : 'black'}>
                {item.price?.updated ? DateTime.fromISO(item.price.updated).toFormat('dd.LL.yyyy') : ''}
              </BodyText>
            )
          ) : (
            <PriceVatInput priceVat={priceVat} onPriceVatChange={handlePriceVatChange} />
          )}
        </Table.Cell>
        {!salesChannelUsesDynamicPricing && (
          <Table.Cell width="112px">
            <PriceSaveButton loading={loading} bundleId={id} priceChanged={priceChanged} onPriceSave={handleSaveClick} />
          </Table.Cell>
        )}
      </Table.Row>
      {children}
    </>
  );
};

export default BundleListItem;
